'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Loader2 } from 'lucide-react';
import { TEAM_MEMBERS, type TaskStatus, type Priority } from '@/lib/mockData';

interface FormValues {
  title: string;
  priority: Priority;
  assigneeId: string;
  dueDate: string;
}

interface AddTaskModalProps {
  column: TaskStatus;
  onClose: () => void;
  onAdd: (title: string, priority: Priority, assigneeId: string, dueDate: string) => void;
}

export default function AddTaskModal({ column, onClose, onAdd }: AddTaskModalProps) {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: '',
      priority: 'Medium',
      assigneeId: 'user-001',
      dueDate: '2026-06-30',
    },
  });

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 500));
    onAdd(data.title, data.priority, data.assigneeId, data.dueDate);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop">
      <div className="bg-card rounded-xl border border-border w-full max-w-md shadow-modal fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">
            Add task to <span className="text-primary">{column}</span>
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors">
            <X size={16} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Task Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('title', { required: 'Task title is required' })}
              placeholder="What needs to be done?"
              autoFocus
              className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all"
            />
            {errors.title && (
              <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Priority</label>
              <select
                {...register('priority')}
                className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Due Date</label>
              <input
                type="date"
                {...register('dueDate', { required: true })}
                className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Assignee</label>
            <select
              {...register('assigneeId')}
              className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30"
            >
              {TEAM_MEMBERS.map((m) => (
                <option key={`assign-opt-${m.id}`} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium min-w-[100px] justify-center"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : 'Add Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}